'use client'

import { PageLayout } from '@/ui/layouts/PageLayout'
import { PageHero } from '@/ui/molecules/page-hero/PageHero'
import { Target } from 'lucide-react'
import { useMemo, useState } from 'react'
import { OpportunitiesContainerSkeleton } from './components/OpportunitiesContainerSkeleton'
import { OpportunitiesFilterBar } from './components/OpportunitiesFilterBar'
import { OpportunitiesInfoCards } from './components/OpportunitiesInfoCards'
import { OpportunitiesList } from './components/OpportunitiesList'
import { formatRate } from './components/shared'
import { useArbitrageOpportunities } from './logic/useArbitrageOpportunities'
import { hasViableSpread } from './types'
import type { ArbitrageOpportunity, OpportunitySortField, OpportunitySourceFilter } from './types'

export function OpportunitiesContainer() {
  const { opportunities, isLoading } = useArbitrageOpportunities()

  const [sourceFilter, setSourceFilter] = useState<OpportunitySourceFilter>('all')
  const [sortField, setSortField] = useState<OpportunitySortField>('spread')
  const [sortAsc, setSortAsc] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Filter
  const filteredOpportunities = useMemo(() => {
    let result = opportunities

    result = result.filter(hasViableSpread)

    // Source filter
    if (sourceFilter !== 'all') {
      result = result.filter((opp) => opp.borrow.source === sourceFilter || opp.deposit.source === sourceFilter)
    }

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        (opp) =>
          opp.asset.toLowerCase().includes(q) ||
          opp.borrow.name.toLowerCase().includes(q) ||
          opp.deposit.name.toLowerCase().includes(q) ||
          opp.borrow.asset.toLowerCase().includes(q) ||
          opp.deposit.asset.toLowerCase().includes(q),
      )
    }

    return result
  }, [opportunities, sourceFilter, searchQuery])

  // Sort
  const sortedOpportunities = useMemo(() => {
    const sorted = [...filteredOpportunities]

    const getSortValue = (opp: ArbitrageOpportunity): number => {
      switch (sortField) {
        case 'spread':
          return opp.spread
        case 'maxTrade':
          return opp.maxTradeUsd ?? 0
        case 'borrowAPY':
          return opp.borrow.borrowAPY
        case 'depositAPY':
          return opp.deposit.depositAPY
        default:
          return opp.spread
      }
    }

    sorted.sort((a, b) => {
      const diff = getSortValue(a) - getSortValue(b)
      return sortAsc ? diff : -diff
    })

    return sorted
  }, [filteredOpportunities, sortField, sortAsc])

  const bestSpread = filteredOpportunities.length > 0 ? filteredOpportunities[0]!.spread : undefined

  if (isLoading) {
    return <OpportunitiesContainerSkeleton />
  }

  return (
    <PageLayout className="relative max-w-6xl gap-4 px-3 sm:gap-6">
      {/* Hero */}
      <div className="relative">
        <PageHero
          title="Opportunities"
          subtitle="Internal arbitrage across HypurrFi products. Borrow low from one market, deposit high in another."
          journey={['Find spread', 'Borrow low', 'Deposit high']}
          stats={[
            { label: 'Opportunities', value: String(filteredOpportunities.length) },
            { label: 'Best Spread', value: `+${formatRate(bestSpread ?? 0)}%`, hidden: bestSpread === undefined },
            { label: 'Strategy', value: 'Rate Arb', icon: <Target className="h-3 w-3 text-muted-foreground" /> },
          ]}

        />
      </div>

      {/* Filter Bar */}
      <OpportunitiesFilterBar
        sourceFilter={sourceFilter}
        onSourceFilterChange={setSourceFilter}
        sortField={sortField}
        onSortFieldChange={setSortField}
        sortAsc={sortAsc}
        onSortAscChange={setSortAsc}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {/* List */}
      <div className="relative">
        {sortedOpportunities.length > 0 ? (
          <OpportunitiesList opportunities={sortedOpportunities} />
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 py-16">
            <p className="text-lg text-white/60">
              {opportunities.length > 0 ? 'No opportunities match your filters' : 'No arbitrage opportunities found'}
            </p>
            <p className="text-sm text-white/40">
              {opportunities.length > 0
                ? 'Try adjusting your filters to see more opportunities'
                : 'All deposit rates are currently below borrow rates'}
            </p>
          </div>
        )}
      </div>

      {/* Info Cards */}
      <div className="relative">
        <OpportunitiesInfoCards />
      </div>
    </PageLayout>
  )
}
